import { Trans, useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { callAPI } from "../../../lib/common";
import Box from "@mui/material/Box";
import { TextField } from "@mui/material";
import LoadingButton from "../../../components/LoadingButton";
import BoxContainer from "./BoxContainer";
import BoxHeading from "./BoxHeading";
import makeStyles from "@mui/styles/makeStyles";

import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../providers/AuthProvider";
import WarningLastSubjectNotSelectedDialog from "../WarningLastSubjectNotSelectedDialog";

const useStyles = makeStyles((theme) => ({
  button: {
    minWidth: "136px",
    paddingRight: 0,
    paddingLeft: 0,
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      minWidth: "96px",
    },
  },
  buttonContainer: {
    marginLeft: theme.spacing(2),
  },
  form: {
    marginBottom: 0,
  },
  formContainer: {
    [theme.breakpoints.down("sm")]: {
      "& > *": {
        flexBasis: "50%",
        flexGrow: 1,
        flexShrink: 1,
      },
    },
  },
  notReceivedCodeLink: {
    color: theme.palette.primary.main,
  },
}));

const EnterCode = ({
  handleSetCodeData,
  handleLoginSuccess,
  codeData,
  setDisplayNotReceivedCodeDialog,
  code,
  setCode,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isVerifying, setIsVerifying] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const [warning, setWarning] = useState(null);
  const [error, setError] = useState(false);

  const theme = useTheme();
  // If we have a GET-code, auto-submit it:
  useEffect(() => {
    if (code) {
      handleSubmit(null);
    }
  }, []);

  useEffect(() => {
    if (codeData?.accessCode) {
      setCode(codeData.accessCode);
    }
    if (codeData?.errorMessage) {
      setError(codeData.errorMessage);
    }
  }, [codeData]);

  const handleCodeChange = (e) => {
    setCode(e.target.value);
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
    }

    if (user?.isSimulated) {
      if (
        !confirm(
          t("Are you sure you want to add the code %1$s to user %2$s?", {
            postProcess: "sprintf",
            sprintf: [code, user.name],
          }),
        )
      ) {
        return;
      }
    }

    setIsVerifying(true);
    setError(false);

    if (isAuthenticated) {
      callAPI("/ajax/prophoto/add_picture_set_by_code.php?code=" + code)
        .then((result) => {
          if (result.error || !result.valid) {
            setError(result.error);
            setIsVerifying(false);
          } else if (result.warning) {
            setIsVerifying(false);
            setWarning(result.warning);
          } else if (result.redirect) {
            document.location.href = result.redirect;
          } else {
            handleLoginSuccess();
          }
        })
        .catch((err) => {
          setIsVerifying(false);
        });
    } else {
      callAPI("/ajax/prophoto/verify_subject_code.php?code=" + code)
        .then((result) => {
          if (result.error || !result.valid) {
            setError(result.errorMessage || result.error);
            setIsVerifying(false);
          } else if (result.data.redirect) {
            document.location.href = result.data.redirect;
          } else {
            handleSetCodeData(result.data);
            setIsVerifying(false);
          }
        })
        .catch((err) => {
          setIsVerifying(false);
        });
    }
  };

  const handleOk = () => {
    // We can really just close the dialog when this happens?
    handleLoginSuccess();
  };

  if (warning) {
    return (
      <WarningLastSubjectNotSelectedDialog
        subject={warning.payload.subject}
        handleOk={handleOk}
      />
    );
  }

  return (
    <BoxContainer>
      <BoxHeading>
        {isAuthenticated ? (
          <Trans>Would you like to add another picture set?</Trans>
        ) : (
          <Trans>Hi! Did you receive a code from us?</Trans>
        )}
      </BoxHeading>

      {user?.isSimulated && (
        <Box mb={2} color="red">
          {t("Simulating user %s / %s", {
            postProcess: "sprintf",
            sprintf: [user.name, user.emailAddress],
          })}
        </Box>
      )}

      <form onSubmit={handleSubmit} className={classes.form}>
        <Box
          display="flex"
          justifyContent="space-between"
          className={classes.formContainer}
        >
          <TextField
            id="front-page-enter-code"
            label={t("Code")}
            variant="outlined"
            onChange={handleCodeChange}
            helperText={error}
            error={!!error}
            value={code}
            size={"medium"}
            color="secondary"
            style={user?.isSimulated ? { backgroundColor: "#ff00004f" } : {}}
            autoFocus
          />
          <Box className={classes.buttonContainer}>
            <LoadingButton
              isLoading={isVerifying}
              variant="primary"
              size={"large"}
              className={classes.button}
              fullWidth
              style={
                user?.isSimulated
                  ? { backgroundColor: "red", color: "white" }
                  : {}
              }
            >
              {isAuthenticated ? (
                <Trans>Add pictures</Trans>
              ) : (
                <Trans>Continue</Trans>
              )}
            </LoadingButton>
          </Box>
        </Box>
        <Box
          mt={2}
          display="flex"
          justifyContent="center"
          position="relative"
          top={
            theme.breakpoints.down("sm") ? theme.spacing(1) : theme.spacing(2)
          }
        >
          <a
            href="#"
            className={classes.notReceivedCodeLink}
            onClick={() => setDisplayNotReceivedCodeDialog(true)}
          >
            <Trans>Not received a code?</Trans>
          </a>
        </Box>
      </form>
    </BoxContainer>
  );
};

export default EnterCode;
